import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { api, fmtMoney } from '../lib/api.js';
import { BatchDistributionPreview } from '../components/BatchDrillIn.jsx';

export default function Reports() {
  const { isOwner } = useOutletContext();
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState('segment');

  const tabs = [
    { id: 'segment', label: 'Segment P&L' },
    ...(isOwner ? [
      { id: 'batch',    label: 'Batch P&L' },
      { id: 'distrib',  label: 'Distributions' },
      { id: 'total',    label: 'Total P&L' },
      { id: 'projects', label: 'Projects' },
      { id: 'sched-f',  label: 'Schedule F packet' },
      { id: '1099',     label: '1099 watch' },
    ] : []),
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-serif text-olive-900">Reports</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm">Year:</label>
          <select className="input w-28" value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </header>

      <nav className="border-b border-olive-100/60 flex gap-1 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.id ? 'border-gold-500 text-olive-900' : 'border-transparent text-ink-700/70 hover:text-olive-900'}`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'segment'  && <SegmentPL year={year} />}
      {tab === 'batch'    && <BatchPL year={year} />}
      {tab === 'distrib'  && <DistributionsTab />}
      {tab === 'total'    && <TotalPL year={year} />}
      {tab === 'projects' && <ProjectsReport />}
      {tab === 'sched-f'  && <ScheduleFExport year={year} />}
      {tab === '1099'     && <Contractor1099 year={year} />}
    </div>
  );
}

function BatchPL({ year }) {
  const [d, setD] = useState(null);
  const [includeRetired, setIncludeRetired] = useState(false);
  useEffect(() => {
    const qs = `year=${year}${includeRetired ? '&all=1' : ''}`;
    api.get(`/api/reports/batch-pl?${qs}`).then(setD);
  }, [year, includeRetired]);
  if (!d) return <Loading />;

  const broilers = d.chicken_batches.filter(b => b.type === 'broiler');
  const layers   = d.chicken_batches.filter(b => b.type === 'layer');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-700/60">
          Per-batch and per-animal P&L for {d.from} → {d.to}. Anything tagged
          to a segment but not to a specific batch / animal rolls up under
          "Segment-level uncategorized" at the bottom.
        </p>
        <label className="inline-flex items-center gap-2 text-sm text-ink-700/70">
          <input type="checkbox" checked={includeRetired} onChange={e => setIncludeRetired(e.target.checked)} />
          Include retired
        </label>
      </div>

      {broilers.length > 0 && <BatchTable title="Broiler batches" rows={broilers} unit="bird" />}
      {layers.length > 0 && <BatchTable title="Layer flocks" rows={layers} unit="hen" />}
      {d.cattle.length > 0 && <CattleTable rows={d.cattle} />}

      <div className="card">
        <h3 className="font-medium text-olive-900 mb-2">Segment-level uncategorized</h3>
        <p className="text-xs text-ink-700/60 mb-2">
          Income / expense rows tagged to one of the three livestock segments
          but not to a specific batch or animal. Re-tag any of these from the
          Transactions register if you want them in the breakdown above.
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
              <th className="text-left py-2 px-3">Segment</th>
              <th className="text-right py-2 px-3">Income</th>
              <th className="text-right py-2 px-3">Expense</th>
              <th className="text-right py-2 px-3">Net</th>
            </tr>
          </thead>
          <tbody>
            {d.segment_uncategorized.map(s => (
              <tr key={s.segment_id} className="border-b border-olive-100/40">
                <td className="py-2 px-3">{s.segment_name}</td>
                <td className="py-2 px-3 text-right text-olive-700">{fmtMoney(s.income_cents)}</td>
                <td className="py-2 px-3 text-right text-rust-700">{fmtMoney(s.expense_cents)}</td>
                <td className={`py-2 px-3 text-right font-semibold ${s.net_cents >= 0 ? 'text-olive-900' : 'text-rust-700'}`}>
                  {fmtMoney(s.net_cents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BatchTable({ title, rows, unit }) {
  const navigate = useNavigate();
  return (
    <div className="card overflow-x-auto">
      <h3 className="font-medium text-olive-900 mb-2">{title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
            <th className="text-left py-2 px-3">Batch</th>
            <th className="text-right py-2 px-3">On hand</th>
            <th className="text-right py-2 px-3">Income</th>
            <th className="text-right py-2 px-3">Expense</th>
            <th className="text-right py-2 px-3">Net</th>
            <th className="text-right py-2 px-3">$ / {unit}</th>
            <th className="text-left py-2 px-3">Status</th>
            <th className="py-2 px-3 w-6"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(b => (
            <tr key={b.id}
                onClick={() => navigate(`/chickens/${b.id}`)}
                className="border-b border-olive-100/40 cursor-pointer hover:bg-cream-100/40">
              <td className="py-2 px-3">
                <div className="font-medium">{b.name}</div>
                <div className="text-xs text-ink-700/60">
                  {b.source_date && `started ${b.source_date}`}{b.expected_processing_date && ` · process ${b.expected_processing_date}`}
                </div>
              </td>
              <td className="py-2 px-3 text-right">{b.on_hand}</td>
              <td className="py-2 px-3 text-right text-olive-700">{fmtMoney(b.income_cents)}</td>
              <td className="py-2 px-3 text-right text-rust-700">{fmtMoney(b.expense_cents)}</td>
              <td className={`py-2 px-3 text-right font-semibold ${b.net_cents >= 0 ? 'text-olive-900' : 'text-rust-700'}`}>
                {fmtMoney(b.net_cents)}
              </td>
              <td className={`py-2 px-3 text-right ${b.per_head_net_cents >= 0 ? 'text-olive-900' : 'text-rust-700'}`}>
                {fmtMoney(b.per_head_net_cents)}
              </td>
              <td className="py-2 px-3 text-xs text-ink-700/70">{b.status}</td>
              <td className="py-2 px-3 text-right text-ink-700/40">›</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CattleTable({ rows }) {
  const navigate = useNavigate();
  return (
    <div className="card overflow-x-auto">
      <h3 className="font-medium text-olive-900 mb-2">Cattle (per animal)</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
            <th className="text-left py-2 px-3">Animal</th>
            <th className="text-left py-2 px-3">Sex</th>
            <th className="text-right py-2 px-3">Income</th>
            <th className="text-right py-2 px-3">Expense</th>
            <th className="text-right py-2 px-3">Net</th>
            <th className="text-left py-2 px-3">Disposition</th>
            <th className="py-2 px-3 w-6"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id}
                onClick={() => navigate(`/cattle/${c.id}`)}
                className="border-b border-olive-100/40 cursor-pointer hover:bg-cream-100/40">
              <td className="py-2 px-3">
                <div className="font-medium">{c.ear_tag || `#${c.id}`}{c.name ? ` — ${c.name}` : ''}</div>
              </td>
              <td className="py-2 px-3 text-xs text-ink-700/70">{c.sex || '—'}</td>
              <td className="py-2 px-3 text-right text-olive-700">{fmtMoney(c.income_cents)}</td>
              <td className="py-2 px-3 text-right text-rust-700">{fmtMoney(c.expense_cents)}</td>
              <td className={`py-2 px-3 text-right font-semibold ${c.net_cents >= 0 ? 'text-olive-900' : 'text-rust-700'}`}>
                {fmtMoney(c.net_cents)}
              </td>
              <td className="py-2 px-3 text-xs text-ink-700/70">{c.disposition}</td>
              <td className="py-2 px-3 text-right text-ink-700/40">›</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SegmentPL({ year }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/api/reports/segment-pl?year=${year}`).then(setD); }, [year]);
  if (!d) return <Loading />;
  const totalNet = d.segments.reduce((s, x) => s + x.net_cents, 0);

  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
            <th className="text-left py-2 px-3">Segment</th>
            <th className="text-right py-2 px-3">Income</th>
            <th className="text-right py-2 px-3">Expense</th>
            <th className="text-right py-2 px-3">Net</th>
          </tr>
        </thead>
        <tbody>
          {d.segments.map(s => (
            <tr key={s.segment_id} className="border-b border-olive-100/40">
              <td className="py-2 px-3 font-medium">{s.name}</td>
              <td className="py-2 px-3 text-right text-olive-700">{fmtMoney(s.income_cents)}</td>
              <td className="py-2 px-3 text-right text-rust-700">{fmtMoney(s.expense_cents)}</td>
              <td className={`py-2 px-3 text-right font-semibold ${s.net_cents >= 0 ? 'text-olive-900' : 'text-rust-700'}`}>
                {fmtMoney(s.net_cents)}
              </td>
            </tr>
          ))}
        </tbody>
        {d.segments.length > 1 && (
          <tfoot>
            <tr className="font-semibold bg-cream-100">
              <td className="py-2 px-3">Sum (visible)</td>
              <td className="py-2 px-3"></td>
              <td className="py-2 px-3"></td>
              <td className={`py-2 px-3 text-right ${totalNet >= 0 ? 'text-olive-900' : 'text-rust-700'}`}>
                {fmtMoney(totalNet)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function TotalPL({ year }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/api/reports/total-pl?year=${year}`).then(setD); }, [year]);
  if (!d) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Summary label="Income (YTD)" value={fmtMoney(d.income.total_cents)} tone="olive" />
        <Summary label="Expense (YTD)" value={fmtMoney(d.expense.total_cents)} tone="rust" />
        <Summary label="Net (YTD)" value={fmtMoney(d.net_cents)} tone={d.net_cents >= 0 ? 'olive' : 'rust'} />
      </div>

      <SchedFTable title="Income by Schedule F line" rows={d.income.by_code} tone="olive" total={d.income.total_cents} />
      <SchedFTable title="Expense by Schedule F line" rows={d.expense.by_code} tone="rust" total={d.expense.total_cents} />
    </div>
  );
}

function SchedFTable({ title, rows, tone, total }) {
  return (
    <div className="card overflow-x-auto">
      <h3 className="font-medium text-olive-900 mb-2">{title}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
            <th className="text-left py-1.5 px-2 w-10">Line</th>
            <th className="text-left py-1.5 px-2">Description</th>
            <th className="text-right py-1.5 px-2">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={3} className="text-ink-700/60 italic py-2 px-2">— none —</td></tr>
          ) : rows.map(r => (
            <tr key={r.schedule_f_code || 'unk'} className="border-b border-olive-100/40">
              <td className="py-1.5 px-2">L{r.schedule_f_line ?? '?'}</td>
              <td className="py-1.5 px-2">{r.description || <span className="italic text-ink-700/50">uncategorized</span>}</td>
              <td className={`py-1.5 px-2 text-right ${tone === 'olive' ? 'text-olive-700' : 'text-rust-700'}`}>{fmtMoney(r.total || 0)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold bg-cream-100">
            <td colSpan={2} className="py-1.5 px-2">Total</td>
            <td className={`py-1.5 px-2 text-right ${tone === 'olive' ? 'text-olive-900' : 'text-rust-700'}`}>{fmtMoney(total)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ProjectsReport() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/api/reports/projects').then(setD); }, []);
  if (!d) return <Loading />;
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
            <th className="text-left py-2 px-3">Project</th>
            <th className="text-left py-2 px-3">Status</th>
            <th className="text-right py-2 px-3">Budget</th>
            <th className="text-right py-2 px-3">Spent</th>
            <th className="text-right py-2 px-3">USDA</th>
            <th className="text-right py-2 px-3">Net to farm</th>
          </tr>
        </thead>
        <tbody>
          {d.projects.map(p => (
            <tr key={p.id} className="border-b border-olive-100/40">
              <td className="py-2 px-3 font-medium">{p.name}</td>
              <td className="py-2 px-3 text-ink-700/70">{p.status}</td>
              <td className="py-2 px-3 text-right">{fmtMoney(p.budget_cents)}</td>
              <td className="py-2 px-3 text-right text-rust-700">{fmtMoney(p.spent_cents)}</td>
              <td className="py-2 px-3 text-right text-olive-700">{fmtMoney(p.reimbursed_cents)}</td>
              <td className={`py-2 px-3 text-right font-semibold ${p.budget_cents != null && p.net_cents > p.budget_cents ? 'text-rust-700' : 'text-olive-900'}`}>
                {fmtMoney(p.net_cents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScheduleFExport({ year }) {
  return (
    <div className="card space-y-3">
      <h3 className="font-medium text-olive-900">Schedule F packet ({year})</h3>
      <p className="text-sm text-ink-700/70">
        One-click CSV your CPA pastes into the return. Income and expense, grouped by Schedule F line, totaled.
      </p>
      <a
        href={`/api/reports/schedule-f.csv?year=${year}`}
        className="btn-primary inline-flex"
        download
      >
        ⬇ Download {year} Schedule F CSV
      </a>
      <p className="text-xs text-ink-700/60">
        Format: <span className="font-mono">type, schedule_f_line, code, description, amount_dollars</span>.
      </p>
    </div>
  );
}

function Contractor1099({ year }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/api/reports/contractors-1099?year=${year}`).then(setD); }, [year]);
  if (!d) return <Loading />;
  return (
    <div className="card overflow-x-auto">
      <h3 className="font-medium text-olive-900 mb-2">Contractor 1099-NEC watch ({d.year})</h3>
      <p className="text-xs text-ink-700/60 mb-3">Threshold: {fmtMoney(d.threshold_cents)} YTD. Contractors above need a 1099-NEC at year-end.</p>
      {d.contractors.length === 0 ? (
        <p className="text-sm text-ink-700/60 italic">No contractors on file.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-ink-700/60 border-b border-olive-100">
              <th className="text-left py-2 px-3">Contractor</th>
              <th className="text-center py-2 px-3">W-9</th>
              <th className="text-right py-2 px-3">YTD paid</th>
              <th className="text-center py-2 px-3">1099?</th>
            </tr>
          </thead>
          <tbody>
            {d.contractors.map(c => (
              <tr key={c.id} className="border-b border-olive-100/40">
                <td className="py-2 px-3">{c.name}</td>
                <td className="py-2 px-3 text-center">{c.w9_on_file ? '✓' : <span className="text-rust-700">missing</span>}</td>
                <td className="py-2 px-3 text-right">{fmtMoney(c.ytd_cents)}</td>
                <td className="py-2 px-3 text-center">
                  {c.flag_1099 ? <span className="pill bg-rust-500/15 text-rust-700">FILE 1099</span> : <span className="text-ink-700/40">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Summary({ label, value, tone }) {
  const c = tone === 'rust' ? 'text-rust-700' : tone === 'olive' ? 'text-olive-900' : 'text-ink-900';
  return (
    <div className="card text-center">
      <div className="text-xs uppercase tracking-wider text-ink-700/60">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${c}`}>{value}</div>
    </div>
  );
}

function Loading() { return <div className="text-ink-700/60 italic py-4">Loading…</div>; }

// Compact stat cell used inside DistributionSegmentCard.
function Cell({ label, value, tone, bold }) {
  const c = tone === 'rust' ? 'text-rust-700' : tone === 'olive' ? 'text-olive-900' : 'text-ink-900';
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-700/60">{label}</div>
      <div className={`mt-0.5 ${c} ${bold ? 'font-semibold' : ''}`}>{value}</div>
    </div>
  );
}

// ─── Distributions tab ───────────────────────────────────────────────────────

function DistributionsTab() {
  const [pools, setPools] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/capex/pools').then(d => setPools(d.pools || [])).catch(e => setErr(e.message));
  }, []);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;

  const segs = [
    { id: 'meat_chickens', label: '🍗 Broilers' },
    { id: 'egg_chickens',  label: '🥚 Layer eggs' },
    { id: 'cattle',        label: '🐄 Beef' },
  ];

  return (
    <div className="space-y-4">
      {pools.length > 0 && (
        <div className="card grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pools.map(p => (
            <div key={p.id} className="text-sm">
              <div className="label">CapEx pool — {p.name}</div>
              <div className={`text-2xl font-semibold ${p.balance_cents > 0 ? 'text-rust-700' : 'text-olive-700'}`}>
                {fmtMoney(p.balance_cents)}
              </div>
              <div className="text-xs text-ink-700/60">
                {p.balance_cents > 0
                  ? `${fmtMoney(p.balance_cents)} still owed — Tier 1 active`
                  : 'Paid off — Tier 2 active (60/40 operator/Jeff)'}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-ink-700/70">
        Each segment shows its YTD waterfall. Expand <strong>Override percentages</strong> to
        try what-ifs — overrides are preview-only and never persisted. To change defaults,
        edit them in Settings.
      </p>

      <div className="space-y-3">
        {segs.map(s => (
          <div key={s.id} className="space-y-1">
            <div className="text-sm font-medium text-ink-700">{s.label}</div>
            <BatchDistributionPreview scope={null} segmentId={s.id} />
          </div>
        ))}
      </div>
    </div>
  );
}

// DistributionSegmentCard was replaced by <BatchDistributionPreview scope={null} />
// from components/BatchDrillIn.jsx — the shared component gives us inline
// override controls for free.
